import React, { useState , useEffect, useRef, useCallback, useMemo } from 'react';
import swal from '@sweetalert/with-react';
import { API } from "aws-amplify";

import { filterArrayOfObjects, useDebouncedValue, sortArrayOfObjects, calculateConflicts, isPrimaryCourseRequest, isAlternateCourseRequest } from '../../../js/Helpers';

import SortToggle from '../../SortToggle';
import Toggle from '../../Toggle';
import SchoolDataFilter, { applySchoolDataFilters } from '../../SchoolDataFilter';
import UploadModal from '../../UploadModal';
import Portal from '../../Portal';

import StudentRequests from '../reports/student_requests';
import BulkEditCourseRequest from '../../BulkEditCourseRequest';
import { NO_DEPARTMENT_FILTER_VALUE, prependNoDepartmentFilterOption } from '../utils/departmentFilterUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faChevronDown, faChevronUp, faCheckSquare, faUpload, faMinusSquare, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';
import { filterOutConflicts } from '../../../js/Conflicts';
import { CONFLICT_TYPES } from '../../../js/Constants';
import { useConflictActions, useConflictData } from '../store/magnetboardStore';
import { saveCourseRequestInclusion, setCourseRequestInclusionInStudents } from '../utils/courseRequestInclusionHelpers';

const buildRequestSelectionKey = (group_id, student_id) => JSON.stringify([`${group_id}`, `${student_id}`]);

export default function DatabaseDataStudentRequests({ schoolInfo, scheduleInfo, coursesLoading, courses, studentsLoading, students, setStudents, sections, setSections, departments, handlePullNewData, readOnly = false, departmentFilterOptions = departments, showDepartmentFilter = true}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [filterActive, setFilterActive] = useState(false);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);

	const [selectedStudents, setSelectedStudents] = useState([]);
	const [selectedCourseID, setSelectedCourseID] = useState(null);
	const [showUploadModal, setShowUploadModal] = useState(false);

	const [toggledCourses, setToggledCourses] = useState([]);
	const [selectedData, setSelectedData] = useState([]);
	const [inclusionRequestsSaving, setInclusionRequestsSaving] = useState([]);

	const filteredResultsRef = useRef(filteredResults);
	filteredResultsRef.current = filteredResults;
	const departmentFilterOptionsWithNoDepartment = useMemo(() => prependNoDepartmentFilterOption(departmentFilterOptions), [departmentFilterOptions]);
	const schoolDataFilterCategories = useMemo(() => {
		const categories = [];
		if(showDepartmentFilter)
		{
			categories.push({
				key: 'departments',
				title: 'Departments',
				dataKey: 'departments',
				noValueFilterValue: NO_DEPARTMENT_FILTER_VALUE,
				options: departmentFilterOptionsWithNoDepartment.map(department_info => ({
					value: department_info.department_id,
					display: department_info.department,
				})),
			});
		}
		return categories;
	}, [departmentFilterOptionsWithNoDepartment, showDepartmentFilter]);

	const { setConflicts, setMatchPercent, markConflictDataFresh } = useConflictActions();
	const { conflicts } = useConflictData();
	
	/////////////////////////////
	//// FRONT END FUNCTIONS ////
	/////////////////////////////
	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsSearching(false);
		}
	}

	const toggleCourse = (course_id) => {
		const course_index = toggledCourses.findIndex(course => course === course_id);

		if(course_index !== -1)
		{
			toggledCourses.splice(course_index, 1);
		}
		else
		{
			toggledCourses.push(course_id);
		}

		setSelectedStudents([]);
		setToggledCourses([...toggledCourses]);
	}

	const toggleSelectCourse = (course_id) => {
		setSelectedData(prev_selected_data => {
			const course_id_string = `${course_id}`;
			return prev_selected_data.includes(course_id_string)
				? prev_selected_data.filter(selected_course_id => selected_course_id !== course_id_string)
				: [...prev_selected_data, course_id_string];
		});
	}

	const toggleSelectAll = () => {
		const any_courses_selected = selectedData.length > 0;

		if(any_courses_selected)
		{
			setSelectedData([]);
		}
		else
		{
			setSelectedData(displayResults.map(course => `${course.course_id}`));
		}
	}

	const toggleSelectStudent = (composite_key) => {
		const student_selected_index = selectedStudents.findIndex(key => key === composite_key);

		if(student_selected_index !== -1)
		{
			selectedStudents.splice(student_selected_index, 1);
		}
		else
		{
			selectedStudents.push(composite_key);
		}

		setSelectedStudents([...selectedStudents]);
	}
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && isLoading) return;
		const search_value = document.getElementsByClassName("school-data-search-bar")[0].value;
		
		if(search_value.length > 1)
		{
			const filtered_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['name', 'course_code']);
			setDisplayResults([...filtered_results]);
		}
		else
		{
			setDisplayResults([...filteredResultsRef.current]);
		}
		
		//setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [filteredResultsRef, isLoading]);
	
	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const handleSortClick = (sortedData, sorted_by, sort_asc_desc) => {
		if(sorted_by === 'num_requests')
		{
			const sorted_results = [...displayResults].sort((course_a, course_b) => {
				const requests_a = getStudentsRequestingCourseWithDetails(course_a.course_id).length;
				const requests_b = getStudentsRequestingCourseWithDetails(course_b.course_id).length;
				return sort_asc_desc === 'asc' ? requests_a - requests_b : requests_b - requests_a;
			});
			setDisplayResults(sorted_results);
			return;
		}
		if(sorted_by === 'departments')
		{
			const get_department_sort_text = (department_ids = []) => {
				const normalized_department_ids = Array.isArray(department_ids) ? department_ids : [];
				if(normalized_department_ids.length === 0) return 'No department';

				return normalized_department_ids.map(department_id => {
					const department_info = departments.find(department => `${department.department_id}` === `${department_id}`);
					return department_info ? department_info.department : 'No department';
				}).sort((department_a, department_b) => `${department_a}`.localeCompare(`${department_b}`, undefined, {numeric:true, sensitivity:'base'})).join(', ');
			};
			const sorted_results = [...displayResults].sort((course_a, course_b) => {
				const comparison = get_department_sort_text(course_a.departments).localeCompare(get_department_sort_text(course_b.departments), undefined, {numeric:true, sensitivity:'base'});
				return sort_asc_desc === 'asc' ? comparison : comparison * -1;
			});
			setDisplayResults(sorted_results);
			return;
		}

		setDisplayResults([...sortedData]);
	}

	const handleFilterClick = (selectedFilters = {}) => {
		const filtered_data = applySchoolDataFilters(courses, selectedFilters, schoolDataFilterCategories);
		setFilterActive(Object.keys(selectedFilters).some(filter_key => (selectedFilters[filter_key] || []).length > 0));
		setFilteredResults([...filtered_data]);
	}

	const getStudentsRequestingCourseWithDetails = useCallback((course_id) => {
		return students.reduce((results, student) => {
			(student.student_requests || []).forEach(request_group => {
				if (!request_group.course_requests || request_group.course_requests.length === 0) return;

				request_group.course_requests.forEach(course_request => {
					if (`${course_request.course_id}` === `${course_id}` && course_request.is_deleted === '0') {
						results.push({
							...student,
							request_info: course_request,
							request_group,
							group_id: request_group.group_id
						});
					}
				});
			});

			return results;
		}, []);
	}, [students]);

	const buildBulkDeletePlan = useCallback((course_ids) => {
		const selected_course_ids = new Set(course_ids.map(course_id => `${course_id}`));
		const course_delete_group_ids = new Map();
		const section_student_ids_by_course = new Map();
		let direct_request_count = 0;
		let cascade_additional_request_count = 0;

		const next_students = students.map(student => {
			const student_request_groups = Array.isArray(student.student_requests) ? student.student_requests : [];
			let student_changed = false;

			const next_request_groups = student_request_groups.reduce((results, request_group) => {
				const course_requests = Array.isArray(request_group.course_requests) ? request_group.course_requests : [];
				const active_selected_requests = course_requests.filter(course_request => course_request && course_request.is_deleted === '0' && selected_course_ids.has(`${course_request.course_id}`));

				if(active_selected_requests.length === 0)
				{
					results.push(request_group);
					return results;
				}

				student_changed = true;
				direct_request_count += active_selected_requests.length;

				const primary_selected_request = active_selected_requests.find(course_request => `${course_request.order}` === '1');

				if(primary_selected_request)
				{
					const primary_course_id = `${primary_selected_request.course_id}`;

					if(!course_delete_group_ids.has(primary_course_id)) course_delete_group_ids.set(primary_course_id, new Set());
					course_delete_group_ids.get(primary_course_id).add(`${request_group.group_id}`);

					if(!section_student_ids_by_course.has(primary_course_id)) section_student_ids_by_course.set(primary_course_id, new Set());
					section_student_ids_by_course.get(primary_course_id).add(`${student.student_id}`);

					cascade_additional_request_count += course_requests.filter(course_request => course_request && course_request.is_deleted === '0' && !selected_course_ids.has(`${course_request.course_id}`)).length;
					return results;
				}

				active_selected_requests.forEach(course_request => {
					const course_id = `${course_request.course_id}`;

					if(!course_delete_group_ids.has(course_id)) course_delete_group_ids.set(course_id, new Set());
					course_delete_group_ids.get(course_id).add(`${request_group.group_id}`);

					if(!section_student_ids_by_course.has(course_id)) section_student_ids_by_course.set(course_id, new Set());
					section_student_ids_by_course.get(course_id).add(`${student.student_id}`);
				});

				const remaining_course_requests = course_requests.filter(course_request => !(course_request && course_request.is_deleted === '0' && selected_course_ids.has(`${course_request.course_id}`)));
				const active_remaining_requests = remaining_course_requests
					.filter(course_request => course_request && course_request.is_deleted === '0')
					.sort((a, b) => Number(a.order) - Number(b.order))
					.map((course_request, index) => ({...course_request, order: `${index + 1}`}));
				const inactive_remaining_requests = remaining_course_requests.filter(course_request => course_request && course_request.is_deleted === '1');
				const next_course_requests = [...active_remaining_requests, ...inactive_remaining_requests];

				if(next_course_requests.length > 0)
				{
					results.push({...request_group, course_requests: next_course_requests});
				}

				return results;
			}, []);

			return student_changed ? {...student, student_requests: next_request_groups} : student;
		});

		const course_delete_payloads = Array.from(course_delete_group_ids.entries()).map(([course_id, group_ids]) => ({
			course_id,
			group_ids: Array.from(group_ids),
			student_ids: Array.from(section_student_ids_by_course.get(course_id) || []),
		}));

		return {next_students, course_delete_payloads, direct_request_count, cascade_additional_request_count};
	}, [students]);

	const handleBulkDeleteRequests = useCallback(async (course_ids = selectedData) => {
		const normalized_course_ids = (Array.isArray(course_ids) ? course_ids : [course_ids]).map(course_id => `${course_id}`);
		if(normalized_course_ids.length === 0) return;

		const deletion_plan = buildBulkDeletePlan(normalized_course_ids);
		if(deletion_plan.direct_request_count === 0)
		{
			swal({
				title: 'No Requests Found',
				text: 'There are no active requests for the selected courses.',
				icon: 'info'
			});
			setSelectedData([]);
			return;
		}

		const selected_courses = normalized_course_ids.map(course_id => courses.find(course => `${course.course_id}` === `${course_id}`)).filter(course => course);
		const confirmed = await swal({
			title: 'Delete Selected Requests?',
			icon: 'warning',
			dangerMode: true,
			buttons: {
				cancel: {
					text: 'Cancel',
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: 'Delete Requests',
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Do you really want to delete requests for the following courses?</p>
					<div style={{marginLeft:'50px', textAlign:'left'}}>
						{selected_courses.map((course_info, index) => (
							<div key={index}>{course_info.name} ({course_info.course_code})</div>
						))}
					</div>
					<p>
						This will delete <strong>{deletion_plan.direct_request_count}</strong> selected request{deletion_plan.direct_request_count === 1 ? '' : 's'}.
					</p>
					{deletion_plan.cascade_additional_request_count > 0 &&
						<p>
							It will also delete <strong>{deletion_plan.cascade_additional_request_count}</strong> additional alternate request{deletion_plan.cascade_additional_request_count === 1 ? '' : 's'} from groups whose primary request is selected.
						</p>
					}
					<p className='red-text'>This will permanently remove those requests from the database.</p>
				</div>
			)
		});

		if(!confirmed) return;

		const affected_section_ids = [];
		const affected_section_ids_set = new Set();
		const section_student_ids_by_course = deletion_plan.course_delete_payloads.reduce((results, payload) => {
			results[payload.course_id] = new Set(payload.student_ids.map(student_id => `${student_id}`));
			return results;
		}, {});
		const student_locked_sections_by_student = students.reduce((results, student) => {
			results[`${student.student_id}`] = new Set((student.student_locked_sections || []).map(section_id => `${section_id}`));
			return results;
		}, {});

		const new_sections = sections.map(section => {
			const section_course_id = `${section.course_id}`;
			if(!(section_course_id in section_student_ids_by_course)) return section;
			if(`${section.locked}` === '1') return section;

			const students_to_remove = section_student_ids_by_course[section_course_id];
			const current_student_list = Array.isArray(section.student_list) ? section.student_list : [];
			const next_student_list = current_student_list.filter(student_id => {
				const student_id_string = `${student_id}`;
				if(!students_to_remove.has(student_id_string)) return true;

				const locked_sections = student_locked_sections_by_student[student_id_string];
				return locked_sections && locked_sections.has(`${section.section_id}`);
			});

			if(next_student_list.length === current_student_list.length) return section;

			if(!affected_section_ids_set.has(`${section.section_id}`))
			{
				affected_section_ids_set.add(`${section.section_id}`);
				affected_section_ids.push(`${section.section_id}`);
			}

			return {...section, student_list: next_student_list};
		});

		setStudents(deletion_plan.next_students);
		setSections(new_sections);
		setSelectedData([]);
		setSelectedStudents([]);

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-requests';
		let delete_successful = false;
		const myInit = {
			response: true,
			body: JSON.stringify({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				update_type: 'delete',
				delete_payloads: deletion_plan.course_delete_payloads.map(payload => ({
					course_id: payload.course_id,
					group_ids: payload.group_ids
				}))
			}),
		};

		try {
			await API.post(apiName, url, myInit);
			delete_successful = true;
		} catch(e)
		{
			console.log(e);
		}

		if(delete_successful)
		{
			if(affected_section_ids.length > 0)
			{
				const conflicts_to_check = [CONFLICT_TYPES.STUDENT_DOUBLE_BOOKED, CONFLICT_TYPES.STUDENT_CONFLICTING_PERIODS, CONFLICT_TYPES.STUDENT_STUDENT_RESTRICTION, CONFLICT_TYPES.TEACHER_STUDENT_RESTRICTION, CONFLICT_TYPES.COURSE_EXCEEDS_CAPACITY, CONFLICT_TYPES.INCLUSION_TOO_MANY_STUDENTS, CONFLICT_TYPES.INCLUSION_OVER_HALF];

				calculateConflicts({
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					conflicts_to_check: conflicts_to_check,
					match_percent: true
				}).then((conflicts_response) => {
					const conflicts_list = conflicts_response.conflicts;
					const filtered_out_conflicts = filterOutConflicts(conflicts, [], conflicts_to_check);
					setConflicts([...filtered_out_conflicts, ...conflicts_list]);
					setMatchPercent(conflicts_response.match_percent);
					markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
				});
			}
		}
		else
		{
			swal({
				title: 'Requests Were Not Deleted',
				text: 'The course request deletions did not finish successfully. Refresh and try again if needed.',
				icon: 'warning'
			});
		}
	}, [buildBulkDeletePlan, conflicts, courses, markConflictDataFresh, schoolInfo.current_schedule_version_id, schoolInfo.school_id, sections, selectedData, setConflicts, setMatchPercent, setSections, setStudents, students]);

	const updateRequestInclusion = async (student_entry, course_id, next_state) => {
		if(readOnly || `${scheduleInfo?.separate_ICR_codes}` !== '0') return;

		const selection_key = buildRequestSelectionKey(student_entry.group_id, student_entry.student_id);
		const saving_key = `${selection_key}:${course_id}`;
		if(inclusionRequestsSaving.includes(saving_key)) return;

		setInclusionRequestsSaving(current_keys => [...current_keys, saving_key]);
		setStudents(current_students => setCourseRequestInclusionInStudents(current_students, selection_key, course_id, next_state));
		try {
			await saveCourseRequestInclusion({schoolInfo, selectionKey: selection_key, courseID: course_id, isInclusion: next_state});

			calculateConflicts({
				school_id: schoolInfo.school_id,
				schedule_version_id: schoolInfo.current_schedule_version_id,
				match_percent: true,
			}).then(conflicts_response => {
				setConflicts(Array.isArray(conflicts_response?.conflicts) ? conflicts_response.conflicts : []);
				setMatchPercent(conflicts_response?.match_percent);
				markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id, {complete: true});
			});
		} catch(error) {
			console.log(error);
			setStudents(current_students => setCourseRequestInclusionInStudents(current_students, selection_key, course_id, !next_state));
			swal({
				title: 'Inclusion Request Not Updated',
				text: 'The request could not be updated. Please try again.',
				icon: 'warning',
			});
		} finally {
			setInclusionRequestsSaving(current_keys => current_keys.filter(current_key => current_key !== saving_key));
		}
	};

	/////////////////////////////
	//// USE EFFECT FUNCTIONS ////
	/////////////////////////////
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(coursesLoading || studentsLoading || filterActive) return;

		filteredResultsRef.current = Array.isArray(courses) ? courses : [];
		setFilteredResults([...filteredResultsRef.current]);
		setHandleChangeComplete(false);
	}, [courses, coursesLoading, studentsLoading, filterActive]);

	useEffect(() => {
		if(!coursesLoading && !studentsLoading) setIsLoading(false);
	}, [coursesLoading, studentsLoading]);
	
	const row_style = {gridTemplateColumns:'35px 1fr 3fr 1fr 1fr 30px'};

	// console.log('#displayResults', displayResults);
	// console.log('#students', students, JSON.stringify(students));
	//console.log({databaseData});
	//console.log({isLoading});
	
	return (
		<>
		{selectedCourseID && !readOnly &&
			<>
				<Portal windowName="Manage Student Requests" windowWidth="900" windowHeight="700" cleanUpFunction={() => setSelectedCourseID(null)}>
					<StudentRequests schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} sections={sections} setSections={setSections} students={students} setStudents={setStudents} courses={courses} courseID={selectedCourseID} />
				</Portal>
			</>
		}
		{showUploadModal && !readOnly &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Student Requests' uploadAPIName='studentreqdata' addReplaceBool={true} closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			
			<h1 className='school-data-main-heading capitalize'>Student Requests</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-filter-row'>
					<div className='school-data-search-bar-container'>
						<input className='school-data-search-bar' placeholder="Search for a section by course code, course name, or teacher..." value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
						<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
					</div>
					<SchoolDataFilter categories={schoolDataFilterCategories} onChange={handleFilterClick} resultCount={displayResults.length}/>
				</div>
				{!readOnly && <div className='btn btn-icon-left upload-action-btn' onClick={() => setShowUploadModal(true)}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>}
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div></div>
					<div className='school-data-database-header-col'>Course Code <SortToggle handleOnClick={handleSortClick} sortKey='course_code' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Course <SortToggle handleOnClick={handleSortClick} sortKey='name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Department <SortToggle handleOnClick={handleSortClick} sortKey='departments' passedData={displayResults}/></div>
					<div className='school-data-database-header-col school-data-database-header-col-centered'># Requests <SortToggle handleOnClick={handleSortClick} sortKey='num_requests' sortType='number' passedData={displayResults}/></div>
					<div></div>
				</div>
				{displayResults.length > 0 && !readOnly &&
					<div className='select-all-row'>
						<div className={`select-all-container ${selectedData.length > 0 && 'blue-text'}`} onClick={toggleSelectAll}>
							<FontAwesomeIcon icon={selectedData.length > 0 ? faMinusSquare : farSquare}/>
							<FontAwesomeIcon className='select-all-arrow' icon={faSortDown}/>
						</div>
						{selectedData.length > 0 &&
							<div className='small-text'>
								<div className='tooltip' data-tooltip-text='Delete requests' onClick={() => handleBulkDeleteRequests(selectedData)}>
									<div className='red-link'>{`Delete ${selectedData.length} selected`}</div>
								</div>
							</div>
						}
					</div>
				}
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{displayResults.length === 0 &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{displayResults.map((course_info, index) => {
						const course_id = course_info.course_id;
						const course_name = course_info.name;
						const course_code = course_info.course_code;
						const course_departments = course_info.departments;
						const students_requesting_course_with_details = getStudentsRequestingCourseWithDetails(course_id);

						const sorted_students_requesting_course = (students_requesting_course_with_details.length > 0) 
						? sortArrayOfObjects(students_requesting_course_with_details, 'last_name', 'text', 'asc') 
						: [];
					
						const num_students_requesting_course = students_requesting_course_with_details.length;
						const request_type_count = students_requesting_course_with_details.reduce((results, student_entry) => {
						const student_id = student_entry.student_id;
						const request_info = student_entry.request_info;
						
						const is_alternate = !isPrimaryCourseRequest(request_info, student_entry.request_group);
						const is_inclusion = (request_info.is_inclusion === '1');
						
						if(!is_alternate) results.primary_requests.push(student_id);
						if(is_alternate) results.alternate_requests.push(student_id);
						if(is_inclusion) results.inclusion_requests.push(student_id);
						
						return results;
					}, {primary_requests:[], alternate_requests:[], inclusion_requests:[]});
					const course_toggled = toggledCourses.includes(course_id);
					const course_selected = selectedData.includes(`${course_id}`);

						return (
							<div className='school-data-database-row' key={index}>
								<div className='upload-screen-student-request-inner-row' style={row_style}>
									{readOnly ? <div></div> : <FontAwesomeIcon className={course_selected ? 'blue-text' : 'dark-gray-text'} icon={course_selected ? faCheckSquare : farSquare} onClick={() => toggleSelectCourse(course_id)}/>}
									<div>{course_code}</div>
									<div>{course_name}</div>
									<div>
										{course_departments.length === 0 ?
											(
												<div className='school-data-department medium-text' key={index}>No department</div>
											):
											(
												<>
												{course_departments.map((department_id, index) => {
													const department_index = departments.findIndex(department => department.department_id === department_id);
													const department = (department_index !== -1) ? departments[department_index].department : 'No department';
													
													return (
														<div className='school-data-department medium-text' key={index}>{department}</div>
													)
												})}
												</>
											)
										}
									</div>
									<div className='text-align-center'>
										{isLoading ? 
											(
												<div className='loader' style={{height:'20px', width:'20px'}}></div>
											):
											(
												<>
													<div>{request_type_count.primary_requests.length} primary</div>
													{request_type_count.alternate_requests.length > 0 &&
														<div className='medium-text purple-text'>{request_type_count.alternate_requests.length} alternates</div>
													}
													{request_type_count.inclusion_requests.length > 0 &&
														<div className='medium-text orange-text'>{request_type_count.inclusion_requests.length} inclusion</div>
													}
												</>
											)
										}
									</div>
									<FontAwesomeIcon className='gray-link school-data-chevron-right' icon={course_toggled ? faChevronUp : faChevronDown} onClick={()=>{toggleCourse(course_id)}}/>
								</div>
								{course_toggled &&
									<div style={{padding:'10px 30px'}}>
										<div className='upload-screen-request-list'>
											<div className='upload-screen-request-list-inner'>
												<div style={{display:'flex'}}>
													<div style={{flex:'1 1'}}>
														{num_students_requesting_course > 0 && !readOnly &&
															<BulkEditCourseRequest
																courseID={course_id}
																schoolInfo={schoolInfo}
																scheduleInfo={scheduleInfo}
																courses={courses}
																students={students}
																setStudents={setStudents}
																sections={sections}
																setSections={setSections}
																selectedStudents={selectedStudents}
																setSelectedStudents={setSelectedStudents}
															/>
														}
													</div>
													{!readOnly && <div>
														<div className='btn blue-btn btn-small school-data-student-request-btn' onClick={() => setSelectedCourseID(course_id)}>Edit Requests</div>
													</div>}
												</div>
												{num_students_requesting_course > 0 ?
													<div className="modal-example-list">
														{sorted_students_requesting_course.map((student_entry, j) => {
															const student_info = student_entry;
															const request_info = student_entry.request_info;
															const group_id = student_entry.group_id;
															const student_id = student_info.student_id;
															const student_first_name = student_info.first_name;
															const student_last_name = student_info.last_name;

															const request_group = student_entry.request_group;
															const student_request_is_alternate =
																isAlternateCourseRequest(request_info, request_group);
															const student_request_is_lower_order_alternate =
																`${request_info.order}` !== "1";
															const student_request_is_inclusion =
																request_info.is_inclusion === "1";

															let primary_course_name = "";
															if (student_request_is_lower_order_alternate) {
																const primary_course_id = request_group?.course_requests
																	?.find((course_req) => `${course_req.order}` === "1")
																	?.course_id;
																if (primary_course_id) {
																	const primary_course_info = courses.find(
																		(course) =>
																			`${course.course_id}` === `${primary_course_id}`
																	);
																	primary_course_name = primary_course_info ? `${primary_course_info.name} (${primary_course_info.course_code})` : "";
																}
															}

													const composite_key = buildRequestSelectionKey(group_id, student_id);
													const student_selected = selectedStudents.includes(composite_key);
													const inclusion_saving_key = `${composite_key}:${course_id}`;
													const inclusion_request_saving = inclusionRequestsSaving.includes(inclusion_saving_key);

															return (
														<div className={`student-requests-request-line ${!readOnly && `${scheduleInfo?.separate_ICR_codes}` === '0' ? 'student-requests-request-line-with-inclusion' : ''}`} key={j}>
																	{!readOnly && <FontAwesomeIcon
																		className={
																			student_selected ? "blue-text" : "dark-gray-text"
																		}
																		icon={student_selected ? faCheckSquare : farSquare}
																		onClick={() => toggleSelectStudent(composite_key)}
																	/>}
																	<div>
																		<div>
																			{student_last_name}, {student_first_name}
																			{student_request_is_alternate && (
																				<span className="small-text">
																					{" - "}
																					<span className="purple-text">Alternate</span>
																					{primary_course_name && (
																						<>{` for `}<b>{primary_course_name}</b></>
																					)}
																				</span>
																			)}
																			{student_request_is_inclusion && (
																				<span className="small-text orange-text">
																					- Inclusion{" "}
																				</span>
																			)}
																		</div>
															<div className="small-text dark-gray-text">
																{student_info.grade}th grade
															</div>
														</div>
														{!readOnly && `${scheduleInfo?.separate_ICR_codes}` === '0' &&
															<div className='course-request-inclusion-toggle'>
																<span>Inclusion</span>
																<Toggle
																	toggleOn={student_request_is_inclusion}
																	handleChange={(next_state) => updateRequestInclusion(student_entry, course_id, next_state)}
																	isSmall={true}
																	toggleColor='yellow'
																	disabled={inclusion_request_saving}
																/>
															</div>
														}
													</div>
															);
														})}
													</div>
												:
													<div className='upload-screen-empty-requests' style={{marginTop:'10px'}}>No students have requested this course yet!</div>
												}
											</div>
										</div>
									</div>
								}
							</div>
						)
					})}
					</>
				)}
			</div>
		</div>
		</>
	);
}
